import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataContext } from '../../../../context/DataContext';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import ThroughputFieldsGrid from './components/ThroughputFieldsGrid';
import CreditsEngineService, { toNumber } from '../../../../services/CreditsEngineService';
import TablePaginationBar from '../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../hooks';
import HelpIcon from '../../../../components/help/HelpIcon';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

const emptyForm = {
  plan_id: '', name: '', monthly_price: '0', currency: 'USD', included_credits: '0',
  max_users: '', max_connections: '', allow_overage: false, throughput: {}, active: true,
  stripe_price_id: ''
};

const BillingSubscriptionPlansContent = () => {
  const { isPlatformRoot } = useContext(DataContext);
  const [plans, setPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [cursor, setCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Paginación con cursor del backend: `load` trae la primera página y
  // `loadMore` va apilando las siguientes. El pager numerado opera sobre lo
  // acumulado; useTablePagination llama a `loadMore` cuando el usuario
  // entra a la última página cargada y todavía queda cursor.
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { items, nextCursor } = await CreditsEngineService.listSubscriptionPlans();
      setPlans(items);
      setCursor(nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible obtener los planes de suscripción.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const { items, nextCursor } = await CreditsEngineService.listSubscriptionPlans({ cursor });
      setPlans((prev) => [...prev, ...items]);
      setCursor(nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible obtener más planes.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsEditing(false);
    setFormValues(emptyForm);
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setIsEditing(true);
    setFormValues({
      plan_id: item.plan_id,
      name: item.name || item.plan_id,
      monthly_price: String(toNumber(item.monthly_price, 0)),
      currency: item.currency || 'USD',
      included_credits: String(toNumber(item.included_credits, 0)),
      max_users: item.max_users != null ? String(item.max_users) : '',
      max_connections: item.max_connections != null ? String(item.max_connections) : '',
      allow_overage: Boolean(item.allow_overage),
      throughput: item.throughput || {},
      active: item.active !== false,
      stripe_price_id: item.stripe_price_id || ''
    });
    setFormOpen(true);
  };

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!formValues.plan_id.trim()) {
      setSnackbar({ open: true, message: 'El plan_id es requerido.', severity: 'error' });
      return;
    }
    const payload = {
      plan_id: formValues.plan_id.trim(),
      name: formValues.name.trim() || formValues.plan_id.trim(),
      monthly_price: Number(formValues.monthly_price || 0),
      currency: formValues.currency || 'USD',
      included_credits: Number(formValues.included_credits || 0),
      allow_overage: formValues.allow_overage,
      throughput: formValues.throughput,
      active: formValues.active,
      // Solo relevante para planes pagos -- lo resuelve el backend
      // server-side al crear el Checkout Session, nunca se expone al
      // catálogo público (/credits/plans). Vacío = plan sin cobro (ej. free).
      stripe_price_id: formValues.stripe_price_id.trim() || null
    };
    if (formValues.max_users !== '') payload.max_users = Number(formValues.max_users);
    if (formValues.max_connections !== '') payload.max_connections = Number(formValues.max_connections);
    try {
      setIsSaving(true);
      await CreditsEngineService.upsertSubscriptionPlan(payload);
      setSnackbar({ open: true, message: 'Plan guardado correctamente.', severity: 'success' });
      setFormOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar el plan.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await CreditsEngineService.upsertSubscriptionPlan({
        plan_id: item.plan_id,
        name: item.name || item.plan_id,
        monthly_price: toNumber(item.monthly_price, 0),
        currency: item.currency || 'USD',
        included_credits: toNumber(item.included_credits, 0),
        max_users: item.max_users,
        max_connections: item.max_connections,
        allow_overage: Boolean(item.allow_overage),
        throughput: item.throughput || {},
        active: !(item.active !== false),
        // upsert_subscription_plan sobreescribe TODO el item -- omitir esto
        // borraría el price_id de Stripe ya configurado en cada toggle.
        stripe_price_id: item.stripe_price_id || null
      });
      setSnackbar({ open: true, message: item.active !== false ? 'Plan desactivado.' : 'Plan reactivado.', severity: 'success' });
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo cambiar el estado del plan.', severity: 'error' });
    }
  };

  const { pageRows, paginationProps } = useTablePagination(plans, {
    storageKey: 'billingPlans',
    hasMore: Boolean(cursor),
    isLoadingMore,
    onNeedMore: loadMore
  });

  return (
    <PageContainer
      title="Planes de suscripción"
      description="Planes disponibles para las cuentas — créditos incluidos, límites de usuarios/conexiones y throughput por defecto. Visible para root y admin; solo root puede editar."
      actions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
            sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
            Actualizar
          </Button>
          {isPlatformRoot && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
              sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
              Nuevo plan
            </Button>
          )}
        </Stack>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      <Section title="Planes configurados">
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
          </Box>
        ) : plans.length === 0 ? (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 3, textAlign: 'center' }}>
            Todavía no hay planes de suscripción configurados.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>plan_id</TableCell>
                  <TableCell sx={headSx}>Nombre</TableCell>
                  <TableCell sx={headSx} align="right">Precio mensual</TableCell>
                  <TableCell sx={headSx} align="right">Créditos incluidos</TableCell>
                  <TableCell sx={headSx} align="right">Máx. usuarios</TableCell>
                  <TableCell sx={headSx}>Estado</TableCell>
                  {isPlatformRoot && <TableCell sx={headSx} align="right">Acciones</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((item) => (
                  <TableRow key={item.plan_id} hover>
                    <TableCell sx={cellSx}>{item.plan_id}</TableCell>
                    <TableCell sx={cellSx}>{item.name || item.plan_id}</TableCell>
                    <TableCell sx={cellSx} align="right">{item.currency || 'USD'} {toNumber(item.monthly_price).toFixed(2)}</TableCell>
                    <TableCell sx={cellSx} align="right">{toNumber(item.included_credits).toFixed(2)}</TableCell>
                    <TableCell sx={cellSx} align="right">{item.max_users != null ? item.max_users : 'Sin límite'}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip
                        size="small"
                        label={item.active !== false ? 'ACTIVO' : 'INACTIVO'}
                        sx={{
                          backgroundColor: item.active === false ? 'var(--wb-danger-16)' : 'var(--wb-x-bg-22c55e29, rgba(34,197,94,0.16))',
                          color: item.active === false ? 'var(--wb-danger)' : 'var(--wb-success)'
                        }}
                      />
                    </TableCell>
                    {isPlatformRoot && (
                      <TableCell sx={cellSx} align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'var(--wb-blue)' }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.active !== false ? 'Desactivar' : 'Reactivar'}>
                          <span>
                            <Switch size="small" checked={item.active !== false} onChange={() => handleToggleActive(item)} />
                          </span>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {plans.length > 0 && <TablePaginationBar {...paginationProps} />}
      </Section>

      {isPlatformRoot && (
        <Dialog open={formOpen} onClose={() => !isSaving && setFormOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 520 } } }}>
          <DialogTitle>{isEditing ? 'Editar plan' : 'Nuevo plan de suscripción'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="plan_id" value={formValues.plan_id} onChange={handleChange('plan_id')} disabled={isEditing}
                fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="plan_id" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Nombre" value={formValues.name} onChange={handleChange('name')} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="nombre" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Precio mensual" value={formValues.monthly_price} onChange={handleChange('monthly_price')}
                type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="precio-mensual" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Moneda" value={formValues.currency} onChange={handleChange('currency')} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="moneda" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Créditos incluidos por mes" value={formValues.included_credits} onChange={handleChange('included_credits')}
                type="number" inputProps={{ step: '0.01', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="créditos-incluidos-por-mes" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Máximo de usuarios (opcional)" value={formValues.max_users} onChange={handleChange('max_users')}
                type="number" inputProps={{ step: '1', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="máximo-de-usuarios-opcional" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Máximo de conexiones (opcional)" value={formValues.max_connections} onChange={handleChange('max_connections')}
                type="number" inputProps={{ step: '1', min: 0 }} fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="máximo-de-conexiones-opcional" /></InputAdornment>
                ) }}
              />
              <TextField
                label="Stripe Price ID (solo planes pagos)" value={formValues.stripe_price_id} onChange={handleChange('stripe_price_id')}
                placeholder="price_1AbCdEfGhIjKlMnOp" helperText="Crear el Product+Price en el dashboard de Stripe (modo recurrente) y pegar el price_id acá. Vacío para un plan sin cobro (ej. Free)."
                fullWidth size="small" sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="stripe-price-id-solo-planes-pagos" /></InputAdornment>
                ) }}
              />
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FormControlLabel
                  control={<Switch checked={formValues.allow_overage} onChange={(e) => setFormValues((prev) => ({ ...prev, allow_overage: e.target.checked }))} />}
                  label="Permite sobregiro por defecto"
                />
                <HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="permite-sobregiro-por-defecto" />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FormControlLabel
                  control={<Switch checked={formValues.active} onChange={(e) => setFormValues((prev) => ({ ...prev, active: e.target.checked }))} />}
                  label="Plan activo"
                />
                <HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="plan-activo" />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="caption" sx={{ color: 'var(--wb-muted-80)' }}>
                  Límites de throughput por defecto
                </Typography>
                <HelpIcon module="creditos" topic="planes-de-suscripcion" anchor="límites-de-throughput-por-defecto" />
              </Stack>
              <ThroughputFieldsGrid
                value={formValues.throughput}
                onChange={(next) => setFormValues((prev) => ({ ...prev, throughput: next }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setFormOpen(false)} disabled={isSaving}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave} disabled={isSaving}
              startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : null}
              sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const BillingSubscriptionPlans = () => (
  <RequireBillingRole allow="root-or-admin" title="Planes de suscripción">
    <BillingSubscriptionPlansContent />
  </RequireBillingRole>
);

export default BillingSubscriptionPlans;
